import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
} from "@mui/material";
import { db } from "./firebase";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { apiFetch, API_BASE } from "./utils/apiClient";

const PRINT_CREDIT_ACCOUNT = "مطبعة صدى سومر - أبو زينب";

// ✅ بعض الروابط تنحفظ بدون "http(s)://" (مثلاً منسوخة يدويًا) فيفتحها المتصفح كرابط
// داخلي بالموقع نفسه فتطلع صفحة خطأ - نضيف البروتوكول إذا كان ناقص قبل الفتح
const normalizeExternalLink = (url) => {
  if (!url) return url;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const PrintDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [accountingRecords, setAccountingRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "orders"), (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      const sorted = data.sort(
        (a, b) => a.createdAt?.seconds - b.createdAt?.seconds
      );
      setOrders(sorted);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "accounting_pro"), (snapshot) => {
      setAccountingRecords(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsubscribe();
  }, []);

  // ✅ 4 بوكسات: المطلوب (فقط إجمالي الفواتير الآجلة المضافة لحساب صدى سومر) / المدفوع (تسديدات حساب المطبعة) / الخصم (خصومات على الحساب) / المتبقي = المطلوب - المدفوع - الخصم
  const printMoneyStats = useMemo(() => {
    const creditGrossInvoiced = accountingRecords.reduce((sum, r) => {
      if (r.creditAction !== "add_invoice") return sum;
      const accountName = r.creditAccountName || r.accountName;
      if (accountName !== PRINT_CREDIT_ACCOUNT) return sum;
      return sum + Number(r.amountIQD || 0);
    }, 0);

    const totalPaid = accountingRecords.reduce((sum, r) => {
      if (r.creditAction !== "payment") return sum;
      const accountName = r.creditAccountName || r.accountName;
      if (accountName !== PRINT_CREDIT_ACCOUNT) return sum;
      return sum + Number(r.amountIQD || 0);
    }, 0);

    const totalDiscount = accountingRecords.reduce((sum, r) => {
      if (r.creditAction !== "discount") return sum;
      const accountName = r.creditAccountName || r.accountName;
      if (accountName !== PRINT_CREDIT_ACCOUNT) return sum;
      return sum + Number(r.amountIQD || 0);
    }, 0);

    const totalDue = creditGrossInvoiced;

    return {
      totalDue,
      totalPaid,
      totalDiscount,
      totalRemaining: totalDue - totalPaid - totalDiscount,
    };
  }, [accountingRecords]);

  const filtered = orders.filter((order) =>
    order.customerName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const PriorityBadge = ({ priority }) => {
    if (!priority) return null;

    const map = {
      High: { label: "HIGH", bg: "#d32f2f", color: "#fff", border: "#b71c1c", icon: "🔴" },
      Medium: { label: "MEDIUM", bg: "#f9a825", color: "#000", border: "#f57f17", icon: "🟡" },
      Low: { label: "LOW", bg: "#2e7d32", color: "#fff", border: "#1b5e20", icon: "🟢" },
    };

    const p = map[priority];

    return (
      <Box
        sx={{
          px: 2,
          py: 0.8,
          borderRadius: "999px",
          fontWeight: 900,
          fontSize: 14,
          backgroundColor: p.bg,
          color: p.color,
          border: `2px solid ${p.border}`,
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
          letterSpacing: 1,
        }}
      >
        <span>{p.icon}</span>
        <span>{p.label}</span>
      </Box>
    );
  };

  const getMinutesBetween = (fromTimestamp, toTimestamp) => {
    if (!fromTimestamp?.seconds || !toTimestamp?.seconds) return null;
    const minutes = Math.round(
      (toTimestamp.seconds - fromTimestamp.seconds) / 60
    );
    return minutes >= 0 ? minutes : null;
  };

  // ✅ نفس صيغة العرض المستخدمة بباقي الواجهات: أقل من يوم = ساعات ودقائق، أكثر من يوم = أيام وساعات ودقائق
  const formatMinutesToDays = (minutes) => {
    const total = Number(minutes || 0);

    const days = Math.floor(total / (60 * 24));
    const hours = Math.floor((total % (60 * 24)) / 60);
    const mins = total % 60;

    let text = "";
    if (days > 0) text += `${days} يوم `;
    if (hours > 0) text += `${hours} ساعة `;
    if (mins > 0) text += `${mins} دقيقة`;

    return text.trim() || "0 دقيقة";
  };

  // ▶️ تأشير بدء العمل بالطباعة - يفعّل بوتون "تمت الطباعة"
  const handleStartPrint = async (order) => {
    if (!order) return;

    const orderRef = doc(db, "orders", order.id);

    await updateDoc(orderRef, {
      printStarted: true,
      printStartedAt: Timestamp.now(),
      history: [
        ...(order.history || []),
        { status: "بدأ الطباعة", by: "المطبعة", timestamp: Timestamp.now() },
      ],
    });
  };

  const handleSubmit = async (order) => {
    if (!order) return;

    const orderRef = doc(db, "orders", order.id);

    // ✅ مطبعة الشركة (بوابة حقيقية): الطلب محبوس عند المطبعة - نفكه فعليًا للورشة الآن
    if (order.status === "عند المطبعة") {
      const newStatus = "عند برهان";

      await updateDoc(orderRef, {
        status: newStatus,
        printStatus: "مكتمل",
        printSubmittedAt: Timestamp.now(),
        history: [
          ...(order.history || []),
          { status: newStatus, by: "المطبعة", timestamp: Timestamp.now() },
        ],
      });

      // ✅ إشعار الورشة
      try {
        await fetch("http://localhost:8080/send-to-user", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            uid: "art@1234",
            title: "📦 طلب جديد من المطبعة",
            body: `تم إرسال طلب جديد من المطبعة إلى الورشة.`,
          }),
        });
      } catch (error) {
        console.error("❌ فشل إرسال الإشعار إلى الورشة:", error);
      }

      await apiFetch(`${API_BASE}/orders/${order.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      return;
    }

    // ✅ توافقي: طلبات كانت على المسار الموازي القديم (وصلت للورشة فوراً من المصمم)
    // بس نأشر انتهاء الطباعة بدون أي تأثير على حالة الطلب الرئيسية أو الورشة (أصلاً عندها الطلب)
    if (order.printStatus === "طباعة") {
      await updateDoc(orderRef, {
        printStatus: "مكتمل",
        printSubmittedAt: Timestamp.now(),
        history: [
          ...(order.history || []),
          { status: "اكتملت الطباعة", by: "المطبعة", timestamp: Timestamp.now() },
        ],
      });
      return;
    }

    // ⛔ توافقي: طلبات قديمة جداً على المسار السابق (لسه ما وصلت الورشة) - نفس السلوك القديم بالضبط
    const newStatus = "عند برهان";

    const updates = {
      status: newStatus,
      printSubmittedAt: Timestamp.now(),
      history: [
        ...(order.history || []),
        { status: newStatus, by: "المطبعة", timestamp: Timestamp.now() },
      ],
    };

    await updateDoc(orderRef, updates);

    // ✅ إشعار الورشة
    try {
      await fetch("http://localhost:8080/send-to-user", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid: "art@1234",
          title: "📦 طلب جديد من المطبعة",
          body: `تم إرسال طلب جديد من المطبعة إلى الورشة.`,
        }),
      });
    } catch (error) {
      console.error("❌ فشل إرسال الإشعار إلى الورشة:", error);
    }

    await apiFetch(`${API_BASE}/orders/${order.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: newStatus }),
    });
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h5" align="right">
        المطبعة
      </Typography>

      <TextField
        fullWidth
        sx={{ mt: 2, mb: 2 }}
        label="🔍 ابحث عن اسم الزبون"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
        <Box
          sx={{
            flex: 1,
            minWidth: 160,
            p: 2,
            borderRadius: 2,
            textAlign: "center",
            bgcolor: "#1565c0",
            color: "#fff",
          }}
        >
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            💰 مجموع الفواتير
          </Typography>
          <Typography variant="h6" fontWeight="900">
            {printMoneyStats.totalDue.toLocaleString()}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 160,
            p: 2,
            borderRadius: 2,
            textAlign: "center",
            bgcolor: "#2e7d32",
            color: "#fff",
          }}
        >
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            ✅ المبلغ المدفوع
          </Typography>
          <Typography variant="h6" fontWeight="900">
            {printMoneyStats.totalPaid.toLocaleString()}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 160,
            p: 2,
            borderRadius: 2,
            textAlign: "center",
            bgcolor: "#ef6c00",
            color: "#fff",
          }}
        >
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            🏷️ الخصم
          </Typography>
          <Typography variant="h6" fontWeight="900">
            {printMoneyStats.totalDiscount.toLocaleString()}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 160,
            p: 2,
            borderRadius: 2,
            textAlign: "center",
            bgcolor: "#c62828",
            color: "#fff",
          }}
        >
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            ⏳ المبلغ المطلوب
          </Typography>
          <Typography variant="h6" fontWeight="900">
            {printMoneyStats.totalRemaining.toLocaleString()}
          </Typography>
        </Box>
      </Box>

      <Box mt={3}>
        <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
          🟢 الطلبات بانتظار الطباعة
        </Typography>

        {filtered
          .filter((order) => order.printStatus === "طباعة" || order.status === "عند المطبعة")
          .sort((a, b) => (a.printArrivedAt?.seconds || 0) - (b.printArrivedAt?.seconds || 0))
          .map((order) => (
            <Box
              key={order.id}
              sx={{
                mb: 2,
                p: 2,
                bgcolor: "#fffde7",
                borderRadius: 2,
                boxShadow: 2,
                border: "1px solid #fbc02d",
                position: "relative",
              }}
            >
              <Box sx={{ position: "absolute", top: 12, left: 12 }}>
                <PriorityBadge priority={order.priority} />
              </Box>

              <Typography fontWeight="bold">
                👤 {order.customerName}
              </Typography>

              <Typography fontWeight="bold">
                🧩 عدد اللوحات: {order.boardsCount || "—"}
                {order.withDrawing && (
                  <> | 🎨 عدد لوحات الرسم: {order.drawingBoardsCount || "—"}</>
                )}
              </Typography>

              <Typography fontWeight="bold">
                🔗{" "}
                {order.upscaleLink ? (
                  <a href={normalizeExternalLink(order.upscaleLink)} target="_blank" rel="noreferrer">فتح رابط اللوحات (UpScale)</a>
                ) : (
                  "—"
                )}
              </Typography>

              {order.compareBoards?.printedBoard?.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {order.compareBoards.printedBoard.map((url, index) => (
                    <Box
                      key={index}
                      component="a"
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        width: "110px",
                        height: "110px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        border: "1px solid #ddd",
                        background: "#f5f5f5",
                        display: "block",
                      }}
                    >
                      <img
                        src={url}
                        alt={`board-${index}`}
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    </Box>
                  ))}
                </Box>
              )}

              <Typography fontWeight="bold" sx={{ mt: 1 }}>
                📥 استلام الطلب:{" "}
                {order.printArrivedAt
                  ? new Date(order.printArrivedAt.seconds * 1000).toLocaleString()
                  : "—"}
              </Typography>

              {order.printStartedAt && (
                <Typography fontWeight="bold">
                  ▶️ بدأ الطباعة:{" "}
                  {new Date(order.printStartedAt.seconds * 1000).toLocaleString()}
                </Typography>
              )}

              {order.printNote && (
                <Typography fontWeight="bold" color="primary">
                  🖨️ ملاحظات للمطبعة: {order.printNote}
                </Typography>
              )}

              <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                <Button
                  onClick={() => {
                    if (!window.confirm("▶️ هل تريد بدء الطباعة لهذا الطلب؟")) return;
                    handleStartPrint(order);
                  }}
                  variant="contained"
                  color="warning"
                  disabled={!!order.printStartedAt}
                >
                  ▶️ {order.printStartedAt ? "تم بدء الطباعة" : "بدأ الطباعة"}
                </Button>

                <Button
                  onClick={() => {
                    if (!window.confirm("✅ هل أنت متأكد إن الطباعة اكتملت لهذا الطلب؟")) return;
                    handleSubmit(order);
                  }}
                  variant="contained"
                  color="primary"
                  disabled={!order.printStartedAt}
                >
                  ✅ تمت الطباعة
                </Button>
              </Box>
            </Box>
          ))}

        <Typography variant="h6" color="secondary" sx={{ mt: 3, mb: 1 }}>
           الطلبات المطبوعة
        </Typography>

        {filtered
          .filter((order) => order.printSubmittedAt)
          .sort((a, b) => (b.printSubmittedAt?.seconds || 0) - (a.printSubmittedAt?.seconds || 0))
          .map((order) => (
            <Box
              key={order.id}
              sx={{
                mb: 2,
                p: 2,
                bgcolor: "#e8f5e9",
                borderRadius: 2,
                boxShadow: 2,
                border: "1px solid #4caf50",
              }}
            >
              <Typography fontWeight="bold">
                👤 {order.customerName}
              </Typography>

              <Typography fontWeight="bold">
                🔗{" "}
                {order.upscaleLink ? (
                  <a href={normalizeExternalLink(order.upscaleLink)} target="_blank" rel="noreferrer">فتح رابط اللوحات (UpScale)</a>
                ) : (
                  "—"
                )}
              </Typography>

              {order.compareBoards?.printedBoard?.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {order.compareBoards.printedBoard.map((url, index) => (
                    <Box
                      key={index}
                      component="a"
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        width: "110px",
                        height: "110px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        border: "1px solid #ddd",
                        background: "#f5f5f5",
                        display: "block",
                      }}
                    >
                      <img
                        src={url}
                        alt={`board-${index}`}
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    </Box>
                  ))}
                </Box>
              )}

              <Box
                sx={{
                  mt: 1,
                  p: 1.2,
                  borderRadius: 1.5,
                  bgcolor: "#ffffff",
                  border: "1px solid #c8e6c9",
                }}
              >
                <Typography fontWeight="bold">
                  ✅ تمت الطباعة:{" "}
                  {order.printSubmittedAt
                    ? new Date(order.printSubmittedAt.seconds * 1000).toLocaleString()
                    : "—"}
                </Typography>

                <Typography fontWeight="bold">
                  ▶️ بدأ الطباعة:{" "}
                  {order.printStartedAt
                    ? new Date(order.printStartedAt.seconds * 1000).toLocaleString()
                    : "—"}
                </Typography>

                <Typography fontWeight="bold">
                  📥 استلام الطلب:{" "}
                  {order.printArrivedAt
                    ? new Date(order.printArrivedAt.seconds * 1000).toLocaleString()
                    : "—"}
                </Typography>

                {(() => {
                  const minutes = getMinutesBetween(order.printArrivedAt, order.printSubmittedAt);
                  return (
                    <Typography fontWeight="bold" color="primary">
                      ⏱️ المدة: {minutes !== null ? formatMinutesToDays(minutes) : "—"}
                    </Typography>
                  );
                })()}
              </Box>
            </Box>
          ))}
      </Box>
    </Container>
  );
};

export default PrintDashboard;
